import type { IGetActionsData } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { IChallenge } from "@bbj-types/challenge";
import type { TCommunicationTemplate } from "@bbj-types/communication";
import type { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import { type IEmailFormTemplate, SplitContextProvider, createSplitContext, useSplitContext } from "@styleguide";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { Control, UseFormHandleSubmit, UseFormSetValue } from "react-hook-form";
import type { TSendSocialOfferResolver } from "./resolver";

export interface ISendSocialOfferContext {
  handleClose: () => void;
  loading: boolean;
  handleSubmit: UseFormHandleSubmit<TSendSocialOfferResolver>;
  onSubmit: (data: TSendSocialOfferResolver) => Promise<void>;
  control: Control<TSendSocialOfferResolver>;
  setValue: UseFormSetValue<TSendSocialOfferResolver>;
  handleModeChange: (newMode: CommunicationTemplateType) => void;
  offer: ISocialOffer;
  candidate: ICandidate;
  challenges: IChallenge[];
  templates: {
    emailTemplates: IEmailFormTemplate[];
    smsTemplates: Pick<TCommunicationTemplate, "_id" | "content" | "name" | "subject" | "type" | "organisation">[];
  };
  mode: CommunicationTemplateType;
  onSuccess: (
    socialApply: { _id: string; status: string; transitions: ISocialApplyTransition[] },
    action: IGetActionsData
  ) => void;
  attachmentsLoading: boolean;
  setAttachmentsLoading: Dispatch<SetStateAction<boolean>>;
}

const SplitContexts = createSplitContext<ISendSocialOfferContext>({
  handleClose: undefined,
  loading: undefined,
  handleSubmit: undefined,
  onSubmit: undefined,
  control: undefined,
  setValue: undefined,
  handleModeChange: undefined,
  offer: undefined,
  candidate: undefined,
  challenges: undefined,
  templates: undefined,
  mode: undefined,
  onSuccess: undefined,
  attachmentsLoading: undefined,
  setAttachmentsLoading: undefined,
});

export const useSendSocialOfferContext = <Key extends keyof ISendSocialOfferContext>(key: Key) => {
  return useSplitContext(SplitContexts, key);
};

export interface ISendSocialOfferProviderProps {
  children: ReactNode;
  values: ISendSocialOfferContext;
}

export const SendSocialOfferProvider = ({ children, values }: ISendSocialOfferProviderProps) => {
  return (
    <SplitContextProvider contextsMap={SplitContexts} values={values}>
      {children}
    </SplitContextProvider>
  );
};
